import React, { useMemo } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { format } from "sql-formatter";
import { Edit } from "@mui/icons-material";
import { CodeEditor } from "./CodeEditor";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import { PureHighlighter } from "#common/PureHighlighter";
import { Language } from "prism-react-renderer";

export type SqlProps = {
    code: string;
    prettify?: boolean;
    languageMode?: Language;
    onSave?: (content: string) => void;
};

export const SqlHighlighter: React.FC<SqlProps> = ({
    code = "",
    prettify = false,
    languageMode = "sql",
    onSave,
}) => {
    const [codeInternal, setCodeInternal] = React.useState<string>(code);

    const formattedCode = useMemo(() => {
        if (prettify) {
            return format(codeInternal);
        } else {
            return codeInternal;
        }
    }, [codeInternal, prettify]);

    const [isEditMode, setEditMode] = React.useState(false);
    const isEditable = React.useMemo(() => {
        return onSave !== undefined;
    }, [onSave]);

    const edit = React.useCallback(() => {
        setEditMode(true);
    }, []);

    const save = React.useCallback(() => {
        if (isEditable) {
            setEditMode(false);
            onSave!(formattedCode);
            setCodeInternal(code);
        }
    }, [formattedCode, isEditable]);

    const abort = React.useCallback(() => {
        if (isEditable) {
            setEditMode(false);
            setCodeInternal(code);
        }
    }, [code, isEditMode, isEditable]);

    return (
        <Stack direction={"row"}>
            {/*<Box sx={{ position: "relative", height: "6rem" }}>*/}
            <Box sx={{ flexGrow: 1 }}>
                {isEditMode && isEditable ? (
                    <CodeEditor
                        code={formattedCode}
                        languageMode={languageMode}
                        onChange={(newCode: string) => {
                            setCodeInternal(newCode);
                        }}
                    />
                ) : (
                    <PureHighlighter
                        code={formattedCode}
                        languageMode={languageMode}
                    />
                )}
            </Box>
            {!isEditable ? null : (
                <Stack
                    direction={"column"}
                    spacing={1}
                    // sx={{
                    //     backgroundColor: theme =>
                    //         theme.palette.mode === "dark"
                    //             ? darkTheme.plain.backgroundColor
                    //             : lightTheme.plain.backgroundColor,
                    // }}
                >
                    {isEditMode ? (
                        <>
                            <Tooltip title={"Abort and reset"}>
                                <Button
                                    variant={"outlined"}
                                    startIcon={<CancelIcon />}
                                    onClick={() => abort()}
                                >
                                    Abort
                                </Button>
                            </Tooltip>
                            <Tooltip
                                title={
                                    "Saves the query, sends the recommendation for review and initiates the execution of the query"
                                }
                            >
                                <Button
                                    variant={"outlined"}
                                    startIcon={<SaveIcon />}
                                    onClick={() => save()}
                                >
                                    Save
                                </Button>
                            </Tooltip>
                        </>
                    ) : (
                        <Button
                            variant={"outlined"}
                            startIcon={<Edit />}
                            onClick={() => edit()}
                        >
                            Edit
                        </Button>
                    )}
                </Stack>
            )}
        </Stack>
    );
};
